import React from 'react';
import classnames from 'classnames';
import { DateTime } from 'luxon';
import {
  Button,
  Container,
  FormGroup,
  Form,
  Row,
  Col,
  NavItem,
  NavLink,
  Nav,
  TabPane,
  TabContent,
} from 'reactstrap';
import {
  Cache,
  Logger,
} from 'aws-amplify';
import Swal from 'sweetalert2';
import Utils from 'lib/Utils';
import Gravatar from 'react-gravatar';
import AutoResizePage from 'components/AutoResizePage.js';
import ColorNavbar from 'components/Navbars/ColorNavbar.js';
import FullFooter from 'components/Footers/FullFooter.js';
import { ErrorMessage, Formik, Field } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  inviteeName: Yup.string().required(
    'Name is required.'
  ),
  inviteeEmail: Yup.string().email('A valid email is required.').required(
    'Email is required.'
  ),
});

const logger = new Logger('MeetingPage');


class MeetingPage extends AutoResizePage {
  _isMounted = false;

  constructor(props) {
    super(props);
    let meetingId = '';
    const {
      match
    } = props;
    if (match) {
      const {
        params
      } = match;
      if (params && params.meetingId) {
        meetingId = `${params.meetingId}`.toUpperCase();
      }
    }
    const params = new URLSearchParams(props.location.search)
    const externalId = params.get('externalId');
    this.wrapper = null;
    this.contentRef = null;
    this.meetRef = null;
    this.state = {
      countDownStartTime: null,
      countDownEndTime: null,
      countDownDuration: 0,
      profileTab: 1,
      jaasApi: null,
      navBarSize: { width: 10, height: 10 },
      screenSize: { width: 0, height: 0 },
      contentSize: null,
      contentHeight: 0,
      contentWidth: 0,
      meetingId,
      meeting: {
        id: '',
        url: '',
        title: '',
        description: '',
        scheduledStartDate: null,
        scheduledEndDate: null,
        roomAvailableDate: null,
        actualStartDate: null,
        actualEndDate: null,
        startDateTime: null,
      },
      organizer: {
        name: 'James',
        md5: '27e8c01e2e9e3232125308dfcf2a96a4',
      },
      invitee: {
        name: '',
        email: '',
        externalId,
      },
      numberOfTokenRequests: 0,
      meetingActive: false,
      clock: {
        dateTime: DateTime.now(),
        timeout: null,
      }
    };
  }

  requestTokenSession = () => {
    return new Promise(async (resolve, reject) => {
      const {
        numberOfTokenRequests,
        meetingId,
        invitee,
      } = this.state;
      const {
        name,
        email,
        externalId,
      } = invitee;
      const tokenSettingsName = `inviteeMeetingSettings${meetingId}`;
      const tokenSettings = Cache.getItem(tokenSettingsName);
      if (tokenSettings) {
        resolve(tokenSettings);
      } else if (numberOfTokenRequests === 4) {
        this.setState(
          {
            numberOfTokenRequests: numberOfTokenRequests + 1,
          }
        );
        reject(new Error('Maximum number or token requests exceeded.'));
      } else {
        if (!externalId && (!name || !email)) {
          reject(new Error('Email and name are required to join this meeting.'));
        }
        const accessUrl = `https://e5ouwqmuwa.execute-api.us-east-1.amazonaws.com/staging/public/meeting/room/access/${meetingId}`;
        this.setState(
          {
            numberOfTokenRequests: numberOfTokenRequests + 1,
          }
        );
        await fetch(
          accessUrl,
          {
            method: 'post',
            body: JSON.stringify(
              {
                externalId,
                name,
                email,
              }
            ),
          }
        ).then(
          (response) => (response.json())
        ).then(
          (tokenResult) => {
            const {
              error,
              result,
            } = tokenResult;
            if (error) {
              reject(new Error(error.message));
            } else {
              const {
                token,
                appId,
                tokenExpirationDate,
              } = result;
              const expirationDate = DateTime.fromISO(tokenExpirationDate);
              const tokenSettingsName = `inviteeMeetingSettings${meetingId}`;
              const tokenSettings = {
                token,
                roomName: result.id,
                appId,
              };
              Cache.setItem(
                tokenSettingsName,
                tokenSettings,
                { expires: expirationDate.toJSDate().getTime() },
              );
              resolve(tokenSettings);
            }
          }
        );
      }
    });
  }

  joinMeeting = async() => {
    await Utils.withRetry(this.requestTokenSession).then(
      (tokenSession) => {
        const {
          appId,
          roomName,
          token,
        } = tokenSession;
        console.debug('roomName', roomName.startsWith(appId) ? roomName : `${appId}/${roomName}`);
        const jaasApi = new window.JitsiMeetExternalAPI(
          '8x8.vc',
          {
            roomName: roomName.startsWith(appId) ? roomName : `${appId}/${roomName}`,
            jwt: token,
            width: '100%',
            height: '100%',
            parentNode: this.meetRef,
            configOverwrite: {
              prejoinPageEnabled: false,
              startWithAudioMuted: true,
              startWithVideoMuted: true,
            },
          },
        );
        jaasApi.addListener(
          'videoConferenceLeft',
          this.onMeetingExit,
        );
        this.stopClock();
        this.setState(
          {
            jaasApi,
            meetingActive: true,
          },
        );
      }
    ).catch(
      (err) => {
        logger.error(err);
        Swal.fire({
          titleText: 'Error',
          text: err.message,
          icon: 'error',
          customClass: {
            confirmButton: 'btn btn-primary'
          },
          buttonsStyling: false,
        });
      }
    );
  }

  stopMeeting = () => {
    const {
      jaasApi,
    } = this.state;
    if (jaasApi) {
      try {
        jaasApi.removeListener(
          'videoConferenceLeft',
          this.onMeetingExit,
        );
        jaasApi.dispose();
      } catch(err) {
        logger.error(err);
      }
    }
  }

  onMeetingExit = async(e) => {
    this.stopMeeting();
    this.startClock();
    this.setState(
      {
        jaasApi: null,
        meetingActive: false,
      },
    );
  }

  getMeeting = () => {
    return new Promise(async (resolve, reject) => {
      const {
        meetingId,
        invitee,
      } = this.state;
      const {
        externalId,
      } = invitee;
      let meetingDetailsUrl = `https://e5ouwqmuwa.execute-api.us-east-1.amazonaws.com/staging/public/meeting/${meetingId}`;
      if (externalId) {
        meetingDetailsUrl = `${meetingDetailsUrl}?externalId=${externalId}`;
      }
      await fetch(meetingDetailsUrl)
        .then(
          response => (response.json())
        ).then(
          (meetingResult) => {
            const {
              error,
              result,
            } = meetingResult;
            if (error) {
              reject(new Error(error.message));
            } else if (result) {
              const {
                id,
                url,
                title,
                description,
                scheduledStartDate,
                scheduledEndDate,
                roomAvailableDate,
                isInvitee,
                actualStartDate,
                actualEndDate,
              } = result;
              const meeting = {
                id,
                url,
                title,
                description,
                scheduledStartDate: DateTime.fromISO(scheduledStartDate),
                scheduledEndDate: DateTime.fromISO(scheduledEndDate),
                roomAvailableDate: DateTime.fromISO(roomAvailableDate),
                isInvitee,
                actualStartDate: actualStartDate !== null ? DateTime.fromISO(actualStartDate) : null,
                actualEndDate: actualEndDate !== null ? DateTime.fromISO(actualEndDate) : null,
              };
              resolve(meeting);
            }
          }
        );
      }
    )
  }

  updateCurrentTime = () => {
    const dateTime = DateTime.now();
    const timeout = setTimeout(this.updateCurrentTime, 60000);
    if (this._isMounted) {
      const clock = {
        dateTime,
        timeout,
      };
      this.setState(
        {
          clock,
        }
      );
    }
  }

  startClock = () => {
    const {
      clock,
    } = this.state;
    if (clock.timeout === null) {
      this.updateCurrentTime();
    }
  }

  stopClock = (updateState = true) => {
    const {
      clock,
    } = this.state;
    if (clock.timeout !== null) {
      window.clearTimeout(clock.timeout);
      if (updateState) {
        this.setState(
          {
            clock: {
              timeout: null,
              dateTime: DateTime.now(),
            }
          }
        );
      }
    }
  }

  setMeetRef = element => {
    if (
      element === null
      || element === undefined
    ) {
      return;
    }
    this.meetRef = element;
  };

  setWrapperRef = element => {
    if (
      element === null
      || element === undefined
    ) {
      return;
    }
    this.wrapperRef = element;
  };

  setProfileTab = tabNumber => {
    this.setState(
      {
        profileTab: tabNumber,
      }
    );
  }

  onSubmit = (values, _) => {
    const {
      inviteeName,
      inviteeEmail,
    } = values;
    const invitee = {
      name: inviteeName,
      email: inviteeEmail,
    };
    this.setState(
      {
        invitee,
      },
    );
    const expirationDate = DateTime.now().plus({month: 90});
    Cache.setItem(
      'inviteeSettings',
      invitee,
      { expires: expirationDate.toJSDate().getTime() },
    );
    Swal.fire({
      title: 'Name and email updated.',
      icon: 'success',
      showConfirmButton: false,
      toast: true,
      timerProgressBar: true,
      timer: 2000,
      position: 'top-end',
    });
  };

  componentDidMount = () => {
    this._isMounted = true;
    const {
      meetingId
    } = this.state;
    window.addEventListener('resize', this.updateContentSize);
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    document.body.classList.add('contact-page');
    if (this.wrapper) {
      this.wrapper.current.scrollTop = 0;
    }
    const inviteeSettings = Cache.getItem('inviteeSettings');
    if (inviteeSettings) {
      this.setState({
        invitee: inviteeSettings,
      });
    }

    if (meetingId) {
      Utils.withRetry(this.getMeeting).then(
        (meeting) => {
          this.setState(
            {
              meeting,
              meetingId: meeting.id,
              countDownStartTime: meeting.scheduledStartDate.toSeconds(),
              countDownEndTime: meeting.scheduledEndDate.toSeconds(),
            },
          );
          this.startClock();
        }
      ).catch(
        (err) => {
          logger.error(err);
          Swal.fire({
            titleText: 'Error',
            text: err.message,
            icon: 'error',
            customClass: {
              confirmButton: 'btn btn-primary'
            },
            buttonsStyling: false,
          });
        }
      );
    }

    this.updateContentSize();
  }

  componentDidUpdate = (e) => {
    if (e.history.pathname !== e.location.pathname) {
      document.documentElement.scrollTop = 0;
      document.scrollingElement.scrollTop = 0;
      if (this.wrapper) {
        this.wrapper.current.scrollTop = 0;
      }
    }
    this.updateContentSize();
  }

  componentWillUnMount = () => {
    this._isMounted = false;
    this.stopMeeting();
    this.stopClock(false);
    window.removeEventListener('resize', this.updateContentSize);
    document.body.classList.remove('contact-page');
  }

  render() {
    const {
      meeting,
      meetingId,
      contentHeight,
      contentWidth,
      meetingActive,
      invitee,
      clock,
      profileTab,
      organizer,
    } = this.state;
    const {
      name,
      email,
      externalId,
    } = invitee;
    const {
      scheduledStartDate,
      scheduledEndDate,
      actualStartDate,
      roomAvailableDate,
    } = meeting;
    const nameAndEmailRequired = !externalId && (!name || !email);
    let headerMessage = 'The meeting room is avaliable 10 minutes prior to the start of the meeting.';
    let meetingStarted = false;
    let meetingEnded = false;
    let timeUntilMeeting = '';
    let meetingRoomIsAvailable = false;
    if (scheduledStartDate) {
      timeUntilMeeting = scheduledStartDate.toRelative();
      meetingStarted = (actualStartDate && clock.dateTime >= actualStartDate) || (clock.dateTime >= scheduledStartDate);
      meetingEnded = actualStartDate !== null || clock.dateTime > scheduledEndDate;
      meetingRoomIsAvailable = clock.dateTime >= roomAvailableDate && !meetingEnded;
    }
    if (meetingRoomIsAvailable) {
      headerMessage = 'The meeting room is open.';
    } else if (scheduledEndDate !== null && clock.dateTime > scheduledEndDate) {
      headerMessage = 'The requested meeting room is no longer available.';
    }

    return (
      <>
      {
        meetingId ?
        <>
          {(!meetingActive) && (
          <ColorNavbar />
          )}
          <div
            id="meet"
            ref={this.setMeetRef}
            className={ meetingActive ? "d-block" : "d-none" }
            style={{ height: `${contentHeight}px`, width: `${contentWidth}px`}}
          />
          <div className="wrapper" ref={this.setWrapperRef}>
          {(!meetingActive && meeting.id) && (
            <>
              <div className="section">
                <Container>
                  <Row>
                    <Col md="3">
                      <div className="section">
                        <section className="text-center">
                          <Gravatar
                            md5={organizer.md5}
                            size={100}
                            rating="g"
                            default="identicon"
                            className="avatar"
                            protocol="https://"
                          />
                          <h3 className="title">{meeting.id}</h3>
                          <h4>Organizer: {organizer.name}</h4>
                        </section>
                        <section>
                          <p>
                            {headerMessage}
                          </p>
                        </section>
                        <section>
                          <br />
                          <Nav className="flex-column nav-tabs-info" role="tablist">
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: profileTab === 1,
                                })}
                                onClick={(e) => {
                                  e.preventDefault();
                                  this.setProfileTab(1);
                                }}
                                href="#pablo"
                              >
                                <i className="tim-icons icon-notes" /> Info
                              </NavLink>
                            </NavItem>
                            <hr className="line-info" />
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: profileTab === 2,
                                })}
                                onClick={(e) => {
                                  e.preventDefault();
                                  this.setProfileTab(2);
                                }}
                                href="#pablo"
                              >
                                <i className="tim-icons icon-single-02" /> You
                              </NavLink>
                            </NavItem>
                          </Nav>
                        </section>
                      </div>
                    </Col>
                    <Col className="ml-auto" md="8">
                      <div className="section">
                        <TabContent activeTab={"profile" + profileTab}>
                          <TabPane tabId="profile1">
                            <div>
                              <header>
                                <h2 className="text-uppercase">
                                  Meeting Information
                                </h2>
                              </header>
                              <hr className="line-info" />
                              <br />
                              <Row>
                                <Col className="align-self-center" md="3">
                                  Title
                                </Col>
                                <Col className="align-self-center" md="9">
                                  {meeting.title}
                                </Col>
                              </Row>
                              <Row>
                                <Col className="align-self-center" md="3">
                                    Date
                                </Col>
                                <Col className="align-self-center" md="9">
                                  {scheduledStartDate.setLocale('en-US').toLocaleString(
                                    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
                                  )}
                                </Col>
                              </Row>
                              <Row>
                                <Col className="align-self-center" md="3">
                                  Time
                                </Col>
                                <Col className="align-self-center" md="9">
                                  {scheduledStartDate.setLocale('en-US').toLocaleString(DateTime.TIME_SIMPLE)}
                                </Col>
                              </Row>
                              <Row>
                                <Col className="align-self-center" md="3">
                                  Time zone
                                </Col>
                                <Col className="align-self-center" md="9">
                                  {scheduledStartDate.setLocale('en-US').toFormat('ZZZZZ')}
                                </Col>
                              </Row>

                              <Row>
                              {meetingStarted ? (
                                <Col className="align-self-center" md="3">
                                  Meeting started
                                </Col>
                              ) : (
                                <Col className="align-self-center" md="3">
                                  Time until meeting
                                </Col>
                              )}
                                <Col className="align-self-center" md="9">
                                  {timeUntilMeeting}
                                </Col>
                              </Row>

                              <Row className="mt-4">
                                <Col md="6">
                                {(meetingRoomIsAvailable && !nameAndEmailRequired) && (
                                  <Button
                                    className="mt-2 mb-2"
                                    color="info"
                                    type="button"
                                    onClick={this.joinMeeting}
                                  >
                                    Join meeting
                                  </Button>
                                )}
                                </Col>
                              </Row>
                            </div>
                          </TabPane>
                          <TabPane tabId="profile2">
                            <Formik
                              enableReinitialize={true}
                              initialValues={
                                {
                                  inviteeName: name,
                                  inviteeEmail: email,
                                }
                              }
                              validationSchema={validationSchema}
                              onSubmit={this.onSubmit}
                            >
                            {({
                              handleSubmit,
                            }) => (
                              <Form
                                onSubmit={handleSubmit}
                              >
                                <div>
                                  <header>
                                    <h2 className="text-uppercase">
                                      Your Information
                                    </h2>
                                  </header>
                                  <hr className="line-info" />
                                  <br />
                                  <Row>
                                    <Col className="align-self-center" md="3">
                                      <label className="labels" htmlFor="inviteeName">
                                        Name
                                      </label>
                                    </Col>
                                    <Col className="align-self-center" md="9">
                                      <FormGroup>
                                        <Field
                                          className="form-control"
                                          id="inviteeName"
                                          name="inviteeName"
                                          type="text"
                                          placeholder="Enter the your name"
                                        />
                                        <ErrorMessage
                                          name="inviteeName"
                                          render={msg => <div className="text-danger">{msg}</div>}
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>
                                  <Row>
                                    <Col className="align-self-center" md="3">
                                      <label className="labels" htmlFor="inviteeEmail">
                                        Email
                                      </label>
                                    </Col>
                                    <Col className="align-self-center" md="9">
                                      <FormGroup>
                                      <Field
                                          className="form-control"
                                          id="inviteeEmail"
                                          name="inviteeEmail"
                                          type="text"
                                          placeholder="Enter the your email"
                                        />
                                        <ErrorMessage
                                          name="inviteeEmail"
                                          render={msg => <div className="text-danger">{msg}</div>}
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>
                                  <Row className="mt-4">
                                    <Col md="6">
                                      <Button color="info" type="submit">
                                      {invitee.name ? (
                                        <span>
                                          Update
                                        </span>
                                      ) : (
                                        <span>
                                          Save
                                        </span>
                                      )}
                                      </Button>
                                      {(meetingRoomIsAvailable && !nameAndEmailRequired) && (
                                        <Button
                                          className="mt-2 mb-2"
                                          color="info"
                                          type="button"
                                          onClick={this.joinMeeting}
                                        >
                                          Join meeting
                                        </Button>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              </Form>
                            )}
                          </Formik>
                          </TabPane>
                        </TabContent>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            </>
            )}
            {(!meetingActive) && (
            <FullFooter />
            )}
          </div>
        </>
        : <></>
      }
      </>
    );
  }
}

export default MeetingPage;

